import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import Badge from '../components/Badge';
import { couriers } from '../../services/apiClient';

const DefaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

const COURIERS_POLL_VISIBLE_MS = 5000;
const COURIERS_POLL_HIDDEN_MS = 20000;

function isDocumentVisible() {
  if (typeof document === 'undefined') return true;
  return document.visibilityState === 'visible';
}

function createCourierIcon(name = 'Motoboy') {
  const safeName = String(name || 'Motoboy');
  return L.divIcon({
    className: 'courier-marker-icon',
    html: `<div class="courier-marker"><div class="courier-marker-icon-emoji">&#128757;</div><div class="courier-marker-label">${safeName}</div></div>`,
    iconSize: [140, 44],
    iconAnchor: [70, 22],
    popupAnchor: [0, -12]
  });
}

function formatMoneyFromCents(value) {
  const cents = Number(value || 0);
  const reais = Number.isFinite(cents) ? cents / 100 : 0;
  return reais.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDateTime(value) {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '-';
  return dt.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatScheduleLabel(schedule) {
  return String(schedule || 'weekly').toLowerCase() === 'monthly' ? 'Mensal' : 'Semanal';
}

function formatPayoutMethodLabel(method) {
  return String(method || 'pix').toLowerCase() === 'bank' ? 'Conta bancaria' : 'PIX';
}

function readPayoutField(source, key) {
  if (!source || typeof source !== 'object') return null;
  const payoutAccount = source.payout_account && typeof source.payout_account === 'object'
    ? source.payout_account
    : null;
  if (payoutAccount && payoutAccount[key]) return payoutAccount[key];
  if (source[key]) return source[key];
  return null;
}

function renderPayoutAccountPreview(source) {
  const method = String(readPayoutField(source, 'method') || source?.payout_method || 'pix').toLowerCase();
  if (method === 'bank') {
    const bankName = readPayoutField(source, 'bank_name');
    const bankAgency = readPayoutField(source, 'bank_agency');
    const bankAccount = readPayoutField(source, 'bank_account');
    const bankAccountType = readPayoutField(source, 'bank_account_type');
    const parts = [];
    if (bankName) parts.push(bankName);
    if (bankAgency) parts.push(`Ag: ${bankAgency}`);
    if (bankAccount) parts.push(`Conta: ${bankAccount}`);
    if (bankAccountType) parts.push(bankAccountType);
    return parts.length > 0 ? parts.join(' | ') : 'Conta bancaria nao informada';
  }
  const pixKey = readPayoutField(source, 'pix_key');
  return pixKey ? `PIX: ${pixKey}` : 'PIX nao informado';
}

function formatStatusGroup(group) {
  const normalized = String(group || '').toLowerCase();
  if (normalized === 'finished') return 'Finalizada';
  if (normalized === 'cancelled') return 'Cancelada';
  return 'Em espera';
}

function payoutStatusLabel(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'paid') return 'Pago';
  if (normalized === 'cancelled') return 'Cancelado';
  return 'Pendente';
}

function cashReturnStatusLabel(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'overdue') return 'Vencida';
  if (normalized === 'code_generated') return 'Codigo gerado';
  if (normalized === 'settled') return 'Devolvida';
  if (normalized === 'cancelled') return 'Cancelada';
  return 'Pendente';
}

const emptyCourierOrders = {
  courier: null,
  summary: null,
  items: [],
  cash_returns: {
    summary: {
      pending_count: 0,
      pending_total_cents: 0,
      overdue_count: 0,
      overdue_total_cents: 0,
      next_due_at: null,
    },
    items: [],
    is_blocked_for_cash_return: false,
  },
};

export default function AdminCouriers() {
  const mapRef = useRef(null);
  const detailsPanelRef = useRef(null);
  const activeDetailsCourierIdRef = useRef(null);
  const [data, setData] = useState([]);
  const [locations, setLocations] = useState([]);
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [settlingId, setSettlingId] = useState(null);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsCourier, setDetailsCourier] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [detailsFilter, setDetailsFilter] = useState('all');
  const [detailsPayload, setDetailsPayload] = useState(emptyCourierOrders);

  const detailsCourierId = detailsOpen && detailsCourier?.id ? detailsCourier.id : null;

  useEffect(() => {
    activeDetailsCourierIdRef.current = detailsCourierId ? String(detailsCourierId) : null;
  }, [detailsCourierId]);

  useEffect(() => {
    return () => {
      try {
        if (mapRef.current) {
          mapRef.current.remove();
          mapRef.current = null;
        }
      } catch {
        // ignore cleanup errors
      }
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    let inFlight = false;
    let timeoutId = null;
    const syncSelectedCourierFromList = (list, requestedDetailsId) => {
      if (!requestedDetailsId || !Array.isArray(list)) return;
      const refreshedCourier = list.find((item) => String(item.id) === requestedDetailsId);
      if (refreshedCourier) {
        setDetailsCourier(refreshedCourier);
      }
    };

    const loadDashboard = async (requestedDetailsId) => {
      setLoading(true);
      setError('');
      try {
        const [list, realtime] = await Promise.all([
          couriers.list({ status: status === 'all' ? undefined : status }),
          couriers.getRealtimeLocations()
        ]);
        if (!mounted) return;
        setData(Array.isArray(list) ? list : []);
        setLocations(Array.isArray(realtime) ? realtime : []);
        syncSelectedCourierFromList(list, requestedDetailsId);
      } catch {
        if (!mounted) return;
        setError('Erro ao carregar motoboys');
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    };

    const loadCourierDetails = async (requestedDetailsId) => {
      if (!requestedDetailsId) {
        setDetailsLoading(false);
        return;
      }

      setDetailsLoading(true);
      try {
        const details = await couriers.getOrders(requestedDetailsId);
        if (!mounted || activeDetailsCourierIdRef.current !== requestedDetailsId) return;
        setDetailsPayload({
          courier: details.courier || null,
          summary: details.summary || null,
          items: Array.isArray(details.items) ? details.items : [],
          cash_returns: details.cash_returns && typeof details.cash_returns === 'object'
            ? details.cash_returns
            : emptyCourierOrders.cash_returns,
        });
        setDetailsError('');
      } catch {
        if (!mounted || activeDetailsCourierIdRef.current !== requestedDetailsId) return;
        setDetailsPayload(emptyCourierOrders);
        setDetailsError('Erro ao carregar entregas do motoboy');
      } finally {
        if (!mounted || activeDetailsCourierIdRef.current !== requestedDetailsId) return;
        setDetailsLoading(false);
      }
    };

    const schedule = () => {
      if (!mounted) return;
      const delay = isDocumentVisible() ? COURIERS_POLL_VISIBLE_MS : COURIERS_POLL_HIDDEN_MS;
      timeoutId = window.setTimeout(load, delay);
    };

    const load = async () => {
      if (!mounted) return;
      if (!isDocumentVisible() || inFlight) {
        schedule();
        return;
      }
      inFlight = true;
      const requestedDetailsId = detailsCourierId ? String(detailsCourierId) : null;
      try {
        await loadDashboard(requestedDetailsId);
        await loadCourierDetails(requestedDetailsId);
      } finally {
        inFlight = false;
        schedule();
      }
    };

    load();

    return () => {
      mounted = false;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [status, detailsCourierId]);

  async function handleSettle(row) {
    const confirmed = window.confirm(
      `Confirmar pagamento das corridas vencidas do motoboy ${row?.name || ''}?`
    );
    if (!confirmed) return;

    setSettlingId(row.id);
    try {
      const response = await couriers.settleFinance(row.id, { scope: 'due' });
      const [list, realtime] = await Promise.all([
        couriers.list({ status: status === 'all' ? undefined : status }),
        couriers.getRealtimeLocations()
      ]);
      setData(Array.isArray(list) ? list : []);
      setLocations(Array.isArray(realtime) ? realtime : []);
      setError('');

      if (detailsCourierId && String(detailsCourierId) === String(row.id)) {
        try {
          setDetailsLoading(true);
          const details = await couriers.getOrders(row.id);
          setDetailsPayload({
            courier: details.courier || null,
            summary: details.summary || null,
            items: Array.isArray(details.items) ? details.items : [],
            cash_returns: details.cash_returns && typeof details.cash_returns === 'object'
              ? details.cash_returns
              : emptyCourierOrders.cash_returns,
          });
          setDetailsError('');
        } catch {
          setDetailsPayload(emptyCourierOrders);
          setDetailsError('Erro ao carregar entregas do motoboy');
        } finally {
          setDetailsLoading(false);
        }
      }

      alert(`Pagamento registrado. Corridas liquidadas: ${Number(response?.settled_orders || 0)}.`);
    } catch {
      alert('Nao foi possivel registrar o pagamento do motoboy.');
    } finally {
      setSettlingId(null);
    }
  }

  async function handleOpenDetails(row) {
    const selectedCourierId = String(row?.id || '');
    if (!selectedCourierId) return;
    setDetailsCourier(row);
    setDetailsFilter('all');
    setDetailsOpen(true);
    setDetailsLoading(true);
    setDetailsError('');
    setDetailsPayload(emptyCourierOrders);
    activeDetailsCourierIdRef.current = selectedCourierId;
    try {
      const details = await couriers.getOrders(selectedCourierId);
      if (activeDetailsCourierIdRef.current !== selectedCourierId) return;
      setDetailsPayload({
        courier: details.courier || null,
        summary: details.summary || null,
        items: Array.isArray(details.items) ? details.items : [],
        cash_returns: details.cash_returns && typeof details.cash_returns === 'object'
          ? details.cash_returns
          : emptyCourierOrders.cash_returns,
      });
    } catch {
      if (activeDetailsCourierIdRef.current !== selectedCourierId) return;
      setDetailsPayload(emptyCourierOrders);
      setDetailsError('Nao foi possivel carregar as entregas do motoboy.');
    } finally {
      if (activeDetailsCourierIdRef.current === selectedCourierId) {
        setDetailsLoading(false);
      }
    }
  }

  function handleCloseDetails() {
    activeDetailsCourierIdRef.current = null;
    setDetailsOpen(false);
    setDetailsCourier(null);
    setDetailsFilter('all');
    setDetailsPayload(emptyCourierOrders);
    setDetailsError('');
  }

  useEffect(() => {
    if (!detailsOpen) return undefined;

    const handleOutsideClick = (event) => {
      const target = event.target;
      if (detailsPanelRef.current && detailsPanelRef.current.contains(target)) return;
      if (target?.closest?.('.admin-courier-open-details-btn')) return;
      handleCloseDetails();
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        handleCloseDetails();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [detailsOpen]);

  const columns = useMemo(
    () => [
      { key: 'name', header: 'Motoboy' },
      { key: 'email', header: 'Email' },
      {
        key: 'status',
        header: 'Status',
        render: (row) => {
          const variant = row.status === 'active' ? 'success' : row.status === 'blocked' ? 'danger' : 'warning';
          return <Badge variant={variant}>{row.status}</Badge>;
        }
      },
      { key: 'is_online', header: 'Online', render: (row) => (row.is_online ? 'Sim' : 'Nao') },
      { key: 'vehicle_type', header: 'Veiculo' },
      {
        key: 'deliveries_completed_total',
        header: 'Entregas',
        render: (row) => Number(row.deliveries_completed_total || 0)
      },
      {
        key: 'payout_method',
        header: 'Recebimento',
        render: (row) => formatPayoutMethodLabel(row.payout_method)
      },
      {
        key: 'payout_account_preview',
        header: 'Conta/PIX',
        render: (row) => {
          const holderName = readPayoutField(row, 'bank_holder_name');
          return (
            <div style={{ display: 'grid', gap: 2 }}>
              <strong>{renderPayoutAccountPreview(row)}</strong>
              {holderName && <small style={{ color: '#B8B8C5' }}>Titular: {holderName}</small>}
            </div>
          );
        }
      },
      {
        key: 'pending_total_cents',
        header: 'A Receber',
        render: (row) => formatMoneyFromCents(row.unpaid_finalized_cents ?? row.pending_total_cents)
      },
      {
        key: 'next_payment_due_at',
        header: 'Data pagamento',
        render: (row) => {
          const currentSchedule = formatScheduleLabel(row.payout_schedule);
          const requestedSchedule =
            row.payout_schedule_requested && row.payout_schedule_requested !== row.payout_schedule
              ? formatScheduleLabel(row.payout_schedule_requested)
              : null;
          return (
            <div style={{ display: 'grid', gap: 2 }}>
              <strong>{formatDateTime(row.next_payment_due_at)}</strong>
              <span style={{ color: '#B8B8C5', fontSize: 11 }}>
                {requestedSchedule ? `${currentSchedule} (pendente: ${requestedSchedule})` : currentSchedule}
              </span>
            </div>
          );
        }
      },
      {
        key: 'actions',
        header: 'Acoes',
        render: (row) => (
          <button
            className="admin-button secondary"
            onClick={() => handleSettle(row)}
            disabled={settlingId === row.id}
            style={{ minWidth: 120 }}
          >
            {settlingId === row.id ? 'Processando...' : 'Marcar pago'}
          </button>
        )
      },
      {
        key: 'courier_orders',
        header: 'Entregas',
        render: (row) => (
          <button
            className="admin-button secondary admin-courier-open-details-btn"
            onClick={() => handleOpenDetails(row)}
            style={{ minWidth: 120 }}
          >
            Ver entregas
          </button>
        )
      }
    ],
    [settlingId, status]
  );

  const normalizedLocations = useMemo(() => {
    const list = Array.isArray(locations) ? locations : [];
    return list
      .map((loc) => ({
        ...loc,
        lat: Number(loc?.lat),
        lng: Number(loc?.lng)
      }))
      .filter((loc) => Number.isFinite(loc.lat) && Number.isFinite(loc.lng));
  }, [locations]);

  const center = normalizedLocations.length > 0
    ? [normalizedLocations[0].lat, normalizedLocations[0].lng]
    : [-22.5, -52.9];

  const summary = detailsPayload?.summary || null;
  const modalItems = Array.isArray(detailsPayload?.items) ? detailsPayload.items : [];
  const cashReturnsPayload = detailsPayload?.cash_returns && typeof detailsPayload.cash_returns === 'object'
    ? detailsPayload.cash_returns
    : emptyCourierOrders.cash_returns;
  const cashReturnsSummary = cashReturnsPayload?.summary && typeof cashReturnsPayload.summary === 'object'
    ? cashReturnsPayload.summary
    : emptyCourierOrders.cash_returns.summary;
  const cashReturnsItems = Array.isArray(cashReturnsPayload?.items) ? cashReturnsPayload.items : [];
  const filteredModalItems = useMemo(() => {
    if (detailsFilter === 'all') return modalItems;
    return modalItems.filter((item) => item.status_group === detailsFilter);
  }, [modalItems, detailsFilter]);

  return (
    <div className="admin-section">
      <PageHeader
        title="Motoboys"
        subtitle="Monitoramento em tempo real"
        actions={[
          <Link
            key="verification"
            to="/admin/motoboys/verificacoes"
            className="admin-button"
            style={{ textDecoration: 'none' }}
          >
            Aprovar/Reprovar Cadastro
          </Link>
        ]}
      />

      <div className="admin-filters">
        <select className="admin-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">Todos</option>
          <option value="active">Ativos</option>
          <option value="pending">Em analise</option>
          <option value="blocked">Bloqueados</option>
        </select>
      </div>

      <div className="admin-courier-map-wrap">
        <div className="admin-card" style={{ height: 360 }}>
          <MapContainer center={center} zoom={11} style={{ height: '100%', width: '100%', borderRadius: 12 }} whenCreated={(map) => { mapRef.current = map; }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {normalizedLocations.map((loc) => (
              <Marker key={loc.id} position={[loc.lat, loc.lng]} icon={createCourierIcon(loc.name)} />
            ))}
          </MapContainer>
        </div>

        {detailsOpen && (
          <div className="admin-courier-map-overlay" onClick={handleCloseDetails}>
            <div
              ref={detailsPanelRef}
              className="admin-courier-orders-modal admin-courier-orders-panel"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="admin-courier-orders-header">
                <div>
                  <h3 style={{ marginBottom: 4 }}>Entregas do motoboy</h3>
                  <p style={{ color: '#B8B8C5' }}>
                    {detailsCourier?.name || detailsPayload?.courier?.name || 'Motoboy'} | Atualizacao em tempo real a cada 5s
                  </p>
                </div>
                <button className="admin-button secondary" onClick={handleCloseDetails}>
                  Fechar
                </button>
              </div>

              <div className="admin-courier-orders-meta">
                <span>
                  Regra de pagamento: <strong>{formatScheduleLabel(detailsCourier?.payout_schedule || detailsPayload?.courier?.payout_schedule)}</strong>
                </span>
                <span>
                  Proximo pagamento: <strong>{formatDateTime(detailsCourier?.next_payment_due_at || summary?.next_payout_due_at)}</strong>
                </span>
                <span>
                  Recebimento: <strong>{formatPayoutMethodLabel(detailsPayload?.courier?.payout_method || detailsCourier?.payout_method)}</strong>
                </span>
                <span>
                  Dados: <strong>{renderPayoutAccountPreview(detailsPayload?.courier || detailsCourier)}</strong>
                </span>
              </div>

              <div className="admin-tabs">
                <button
                  type="button"
                  className={detailsFilter === 'all' ? 'active' : ''}
                  onClick={() => setDetailsFilter('all')}
                >
                  Todas ({Number(summary?.total_orders || 0)})
                </button>
                <button
                  type="button"
                  className={detailsFilter === 'finished' ? 'active' : ''}
                  onClick={() => setDetailsFilter('finished')}
                >
                  Finalizadas ({Number(summary?.finished_orders || 0)})
                </button>
                <button
                  type="button"
                  className={detailsFilter === 'waiting' ? 'active' : ''}
                  onClick={() => setDetailsFilter('waiting')}
                >
                  Em espera ({Number(summary?.waiting_orders || 0)})
                </button>
                <button
                  type="button"
                  className={detailsFilter === 'cancelled' ? 'active' : ''}
                  onClick={() => setDetailsFilter('cancelled')}
                >
                  Canceladas ({Number(summary?.cancelled_orders || 0)})
                </button>
              </div>

              <div className="admin-courier-orders-stats">
                <div className="admin-card">
                  <h3>Total a receber</h3>
                  <div className="value">{formatMoneyFromCents(summary?.unpaid_courier_value_cents || 0)}</div>
                </div>
                <div className="admin-card">
                  <h3>Pronto para pagamento</h3>
                  <div className="value">{formatMoneyFromCents(summary?.ready_for_payout_cents || 0)}</div>
                </div>
                <div className="admin-card">
                  <h3>Aguardando ciclo</h3>
                  <div className="value">{formatMoneyFromCents(summary?.waiting_next_cycle_cents || 0)}</div>
                </div>
                <div className="admin-card">
                  <h3>Total bruto (finalizadas)</h3>
                  <div className="value">{formatMoneyFromCents(summary?.total_delivery_fee_cents || 0)}</div>
                </div>
                <div className="admin-card">
                  <h3>Taxa ADM descontada</h3>
                  <div className="value">{formatMoneyFromCents(summary?.unpaid_admin_fee_cents || 0)}</div>
                </div>
                <div className="admin-card">
                  <h3>Cash pendente devolucao</h3>
                  <div className="value">{formatMoneyFromCents(cashReturnsSummary?.pending_total_cents || 0)}</div>
                </div>
                <div className="admin-card">
                  <h3>Cash vencido</h3>
                  <div className="value">{formatMoneyFromCents(cashReturnsSummary?.overdue_total_cents || 0)}</div>
                  <small>{Number(cashReturnsSummary?.overdue_count || 0)} pedido(s) vencido(s)</small>
                </div>
              </div>

              {cashReturnsPayload?.is_blocked_for_cash_return ? (
                <div className="admin-error" style={{ marginBottom: 10 }}>
                  Motoboy bloqueado por devolucao cash vencida.
                </div>
              ) : null}

              {detailsLoading && <div className="admin-loading">Carregando entregas...</div>}
              {!detailsLoading && detailsError && <div className="admin-error">{detailsError}</div>}
              {!detailsLoading && !detailsError && (
                filteredModalItems.length > 0 ? (
                  <div className="admin-courier-orders-table-wrap">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Pedido</th>
                          <th>Loja</th>
                          <th>Status</th>
                          <th>Frete</th>
                          <th>Valor motoboy</th>
                          <th>Pagamento</th>
                          <th>Data pedido</th>
                          <th>Data pagamento</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredModalItems.map((item) => {
                          const badgeVariant =
                            item.status_group === 'finished'
                              ? 'success'
                              : item.status_group === 'cancelled'
                                ? 'danger'
                                : 'warning';
                          return (
                            <tr key={item.id}>
                              <td>{item.order_number || '-'}</td>
                              <td>{item.restaurant_name || '-'}</td>
                              <td>
                                <div style={{ display: 'grid', gap: 4 }}>
                                  <Badge variant={badgeVariant}>{formatStatusGroup(item.status_group)}</Badge>
                                  <small style={{ color: '#B8B8C5' }}>{item.status || '-'}</small>
                                </div>
                              </td>
                              <td>{formatMoneyFromCents(item.delivery_fee_cents)}</td>
                              <td>
                                {formatMoneyFromCents(item.courier_value_cents)}
                                {!item.courier_value_confirmed && (
                                  <small style={{ display: 'block', color: '#B8B8C5' }}>estimado</small>
                                )}
                              </td>
                              <td>{payoutStatusLabel(item.payout_status)}</td>
                              <td>{formatDateTime(item.created_at)}</td>
                              <td>{formatDateTime(item.payout_due_at || item.payout_paid_at)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="admin-empty">Nenhuma entrega encontrada para este filtro.</div>
                )
              )}

              {!detailsLoading && !detailsError && (
                <div style={{ marginTop: 14 }}>
                  <h3 style={{ margin: '0 0 8px', color: '#E8E8EF' }}>Devolucao de dinheiro (cash)</h3>
                  {cashReturnsItems.length > 0 ? (
                    <div className="admin-courier-orders-table-wrap">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Pedido</th>
                            <th>Loja</th>
                            <th>Status</th>
                            <th>Valor</th>
                            <th>Prazo</th>
                            <th>Liquidado em</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cashReturnsItems.map((item) => (
                            <tr key={item.id}>
                              <td>{item.order_number || '-'}</td>
                              <td>{item.restaurant_name || '-'}</td>
                              <td>{cashReturnStatusLabel(item.status)}</td>
                              <td>{formatMoneyFromCents(item.amount_total_cents)}</td>
                              <td>{formatDateTime(item.due_at)}</td>
                              <td>{formatDateTime(item.settled_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="admin-empty">Sem pendencias de devolucao cash para este motoboy.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <DataTable columns={columns} data={data} loading={loading} error={error} emptyMessage="Nenhum motoboy encontrado" />
    </div>
  );
}
